import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';

const AboutPage = () => {
  const [orgSlideIndex, setOrgSlideIndex] = useState(0);

  const departments = [
    { name: '전략기획본부', items: ['부동산개발/기획', '사업성/수지 분석', '상품 기획', '인허가/정책 대응'] },
    { name: '관리본부', items: ['경영지원', '입주관리/촉진', '입주인력지원', '광고홍보'] },
    { name: '마케팅본부', items: ['마케팅', '컨설팅', '분양 영업', '고객 관리'] },
    { name: '입주관리본부', items: ['입주행정 지원', 'TM', '교육 양성', '인력 배치'] },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setOrgSlideIndex((prev) => (prev + 1) % departments.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [departments.length]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '회사소개 - GB더플래닛',
    description: '부동산 개발 시장의 새로운 기준을 제시하는 GB더플래닛의 경영철학, 비전, 핵심 경쟁력을 소개합니다',
    url: typeof window !== 'undefined' ? `${window.location.origin}/about` : '',
    mainEntity: {
      '@type': 'Organization',
      name: '주식회사 GB더플래닛',
      description: '부동산 개발·시행·업무대행 전문 기업',
      founder: {
        '@type': 'Person',
        name: '이동준',
        jobTitle: '대표이사',
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />

      <div className="pt-20">
        {/* Hero Section with Upward Building View */}
        <section className="relative h-[70vh] md:h-[80vh] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/6b1a3a57c9242ecc0b48b1b5a880dd63.jpg"
              alt="Building View"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/70"></div>
          </div>

          <div className="relative h-full flex items-center justify-center px-4 md:px-8">
            <div className="text-center text-white max-w-[900px]">
              <AnimatedSection animation="fadeUp">
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
                  GREAT<span className="text-gold">.</span>최고의<br />
                  BUSINESS<span className="text-gold">.</span>사업<br />
                  PARTNER<span className="text-gold">.</span>파트너
                </h1>
              </AnimatedSection>
              <AnimatedSection animation="fadeUp" delay={200}>
                <p className="text-lg md:text-2xl font-light">
                  함께 성장하는 기업<span className="text-gold">.</span>GB더플래닛
                </p>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* CEO Message */}
        <section id="ceo" className="bg-navy py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
              <div className="lg:col-span-12">
                <AnimatedSection animation="fadeRight">
                  <h2 className="text-white text-2xl md:text-3xl font-bold mb-4 md:mb-6 font-english">CEO Message</h2>
                </AnimatedSection>
              </div>
              <div className="lg:col-span-12 flex flex-col justify-center">
                <div className="space-y-4 md:space-y-5 text-white text-sm md:text-base font-light leading-relaxed md:leading-loose">
                  <AnimatedSection animation="fadeLeft" delay={100}>
                    <p>
                      안녕하십니까.<br />
                      GB더플래닛 대표이사 이동준입니다.
                    </p>
                  </AnimatedSection>
                  <AnimatedSection animation="fadeLeft" delay={200}>
                    <p>
                      부동산 개발 시장은 끊임없이 변화하며, 그 속에서 정확한 시장 판단과 리스크 관리, 그리고 수익 구조에 대한 전략적 설계가 사업의 성패를 좌우합니다.
                    </p>
                  </AnimatedSection>
                  <AnimatedSection animation="fadeLeft" delay={300}>
                    <p>
                      <span className="text-gold font-medium">GB더플래닛은 단순한 대행사가 아닌, 고객의 사업 전 과정에 함께하는 전략적 파트너입니다.</span> 저희는 데이터에 기반한 객관적인 분석과 체계적인 실행 프로세스를 바탕으로, 사업의 불확실성을 줄이고 실질적인 성과를 만들어왔습니다.
                    </p>
                  </AnimatedSection>
                  <AnimatedSection animation="fadeLeft" delay={400}>
                    <p>
                      앞으로도 GB더플래닛은 <span className="text-gold font-medium">부동산 개발 시장의 새로운 기준을 제시하며, 투명하고 책임 있는 파트너로서</span> 고객의 성공적인 사업 추진과 지속적인 성장을 함께하겠습니다.
                    </p>
                  </AnimatedSection>
                  <AnimatedSection animation="fadeLeft" delay={500}>
                    <p>감사합니다.</p>
                  </AnimatedSection>
                  <AnimatedSection animation="fadeUp" delay={600}>
                    <div className="pt-6 md:pt-8">
                      <div className="text-gold text-sm md:text-base font-medium mb-1">대표이사</div>
                      <div className="text-white text-lg md:text-xl font-bold">이동준</div>
                    </div>
                  </AnimatedSection>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Competitiveness */}
        <section id="vision" className="bg-gray-50 py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-3xl md:text-4xl font-bold text-navy text-center mb-1 font-english">PHILOSOPHY &amp; VISION</h2>
              <p className="text-center text-gold text-sm md:text-base mb-8 md:mb-12">경영철학 및 비전</p>
            </AnimatedSection>
            
            {/* 모바일: 삼각형 배치 (상단 2개 + 하단 1개 중앙) */}
            <div className="flex flex-col items-center gap-2.5 md:hidden">
              <div className="grid grid-cols-2 gap-2.5 w-full">
                {[
                  {
                    icon: 'ri-user-star-line',
                    title: '전문경영',
                    desc: '대한민국의 부동산 시장을 대표하는 종합부동산 기업',
                  },
                  {
                    icon: 'ri-award-line',
                    title: '책임경영',
                    desc: '소비자 가치 기준에 맞춰 새로운 사고와 새로운 정신으로 마케팅 신화 창조',
                  },
                ].map((item, index) => (
                  <AnimatedSection key={index} animation="scaleUp" delay={index * 150}>
                    <div className="w-full h-[180px] rounded-xl border-2 border-gold p-3 flex flex-col items-center justify-center text-center hover:bg-gold/5 transition-colors">
                      <div className="w-16 h-16 flex items-center justify-center mb-2">
                        <i className={`${item.icon} text-5xl text-navy`}></i>
                      </div>
                      <h3 className="text-base font-bold text-navy mb-1.5 break-words">{item.title}</h3>
                      <p className="text-[10px] text-gray-600 leading-relaxed break-words">{item.desc}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
              <AnimatedSection animation="scaleUp" delay={300}>
                <div className="w-[calc(50vw-1.25rem)] max-w-[180px] h-[180px] rounded-xl border-2 border-gold p-3 flex flex-col items-center justify-center text-center hover:bg-gold/5 transition-colors">
                  <div className="w-16 h-16 flex items-center justify-center mb-2">
                    <i className="ri-shake-hands-line text-5xl text-navy"></i>
                  </div>
                  <h3 className="text-base font-bold text-navy mb-1.5 break-words">인재경영</h3>
                  <p className="text-[10px] text-gray-600 leading-relaxed break-words">아울러 동종업계의 이정표가 되는 생각으로 향상 전적하고 최선을 다하는 기업</p>
                </div>
              </AnimatedSection>
            </div>

            {/* 데스크톱: 3열 그리드 */}
            <div className="hidden md:grid md:grid-cols-3 gap-4 lg:gap-6">
              {[
                {
                  icon: 'ri-user-star-line',
                  title: '전문경영',
                  desc: '대한민국의 부동산 시장을 대표하는 종합부동산 기업',
                },
                {
                  icon: 'ri-award-line',
                  title: '책임경영',
                  desc: '소비자 가치 기준에 맞춰 새로운 사고와 새로운 정신으로 마케팅 신화 창조',
                },
                {
                  icon: 'ri-shake-hands-line',
                  title: '인재경영',
                  desc: '아울러 동종업계의 이정표가 되는 생각으로 향상 전적하고 최선을 다하는 기업',
                },
              ].map((item, index) => (
                <AnimatedSection key={index} animation="scaleUp" delay={index * 150}>
                  <div className="w-full h-[240px] lg:h-[280px] rounded-2xl border-2 border-gold p-5 lg:p-6 flex flex-col items-center justify-center text-center hover:bg-gold/5 transition-colors">
                    <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center mb-3 lg:mb-4">
                      <i className={`${item.icon} text-6xl lg:text-7xl text-navy`}></i>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-navy mb-2 lg:mb-3 break-words">{item.title}</h3>
                    <p className="text-xs lg:text-sm text-gray-600 leading-relaxed break-words">{item.desc}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Core Competitiveness */}
        <section className="bg-white py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-3xl md:text-4xl font-bold text-navy text-center mb-1 font-english">Core Competitiveness</h2>
              <p className="text-center text-gold text-sm md:text-base mb-8 md:mb-12">핵심 경쟁력</p>
            </AnimatedSection>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
              {[
                {
                  title: '정확한 시장 분석',
                  desc: '입지 분석, 수요 예측, 경쟁 환경 분석 등 데이터 기반의 철저한 시장 조사를 통해 사업의 성공 가능성을 높입니다.',
                  icon: 'ri-line-chart-line',
                },
                {
                  title: '체계적인 사업성 검토',
                  desc: '수익성 분석, 리스크 평가, 자금 계획 수립 등 다각도의 사업성 검토를 통해 안정적인 사업 구조를 설계합니다.',
                  icon: 'ri-file-list-3-line',
                },
                {
                  title: '실무 중심의 개발 프로세스',
                  desc: '현장 경험이 풍부한 전문가들이 직접 프로젝트를 관리하며, 효율적이고 체계적인 개발 프로세스를 운영합니다.',
                  icon: 'ri-settings-3-line',
                },
                {
                  title: '분양 성과 중심 전략',
                  desc: '시장 트렌드를 반영한 상품 기획, 타겟 맞춤형 마케팅, 효과적인 영업 전략으로 분양 성공을 이끌어냅니다.',
                  icon: 'ri-megaphone-line',
                },
                {
                  title: '안정적인 사업 관리',
                  desc: '인허가부터 준공까지 전 과정에 걸친 체계적인 관리와 모니터링으로 사업의 안정적인 진행을 보장합니다.',
                  icon: 'ri-shield-check-line',
                },
              ].map((item, index) => (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
                  <div className="group relative bg-navy rounded-2xl p-5 md:p-6 h-full overflow-hidden hover:shadow-2xl transition-all duration-500">
                    {/* 배경 장식 */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-gold/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    
                    <div className="relative z-10">
                      {/* 아이콘 */}
                      <div className="flex items-center justify-start mb-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-gold/20 rounded-xl group-hover:bg-gold/30 transition-colors">
                          <i className={`${item.icon} text-2xl text-gold`}></i>
                        </div>
                      </div>
                      
                      {/* 제목 */}
                      <h3 className="text-lg font-bold text-white mb-3 group-hover:text-gold transition-colors">
                        {item.title}
                      </h3>
                      
                      {/* 설명 */}
                      <p className="text-sm text-white/70 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Organization Chart */}
        <section className="bg-gray-50 py-12 md:py-20">
          <div className="max-w-[1000px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2 md:mb-3 font-english">ORGANIZATION CHART</h2>
              <p className="text-gold text-sm md:text-base mb-8 md:mb-10">조직도</p>
            </AnimatedSection>

            <AnimatedSection animation="scaleUp" delay={200}>
              <div className="flex flex-col items-center">
                {/* GB더플래닛 */}
                <div className="bg-navy text-white px-6 md:px-12 py-2 md:py-3 rounded-lg text-base md:text-lg font-bold">
                  GB더플래닛
                </div>
                
                <div className="w-0.5 h-4 md:h-6 bg-gray-300"></div>
                
                <div className="relative w-full flex justify-center">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-0">
                    <div className="bg-navy text-white px-6 md:px-10 py-2 md:py-3 rounded-lg text-base md:text-lg font-bold">
                      CEO
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-center">
                      <div className="h-4 w-0.5 md:h-0.5 md:w-6 bg-gray-300"></div>
                      <div className="bg-[#C4836A] text-white px-4 md:px-6 py-2 md:py-3 rounded-lg text-sm md:text-base font-bold">
                        감사
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="w-0.5 h-4 md:h-6 bg-gray-300"></div>
                
                <div className="hidden md:block w-[750px] h-0.5 bg-gray-300"></div>
                
                <div className="hidden md:flex justify-between w-[750px]">
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                </div>
                
                {/* Desktop Grid */}
                <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 w-full mt-0">
                  {departments.map((dept, deptIndex) => (
                    <div key={deptIndex} className="flex flex-col items-center">
                      <div className="bg-gold text-navy px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-bold w-full text-center mb-2">
                        {dept.name}
                      </div>
                      <div className="space-y-1.5 w-full">
                        {dept.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="bg-navy/90 text-white px-2 md:px-3 py-2 rounded-lg text-xs text-center"
                          >
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile Slider */}
                <div className="md:hidden w-full mt-4">
                  <div className="overflow-hidden">
                    <div 
                      className="flex transition-transform duration-500 ease-in-out"
                      style={{ transform: `translateX(-${orgSlideIndex * 100}%)` }}
                    >
                      {departments.map((dept, deptIndex) => (
                        <div key={deptIndex} className="flex-shrink-0 w-full px-2">
                          <div className="flex flex-col items-center">
                            <div className="bg-gold text-navy px-4 py-2 rounded-lg text-sm font-bold w-full text-center mb-2">
                              {dept.name}
                            </div>
                            <div className="space-y-1.5 w-full">
                              {dept.items.map((item, itemIndex) => (
                                <div
                                  key={itemIndex}
                                  className="bg-navy/90 text-white px-3 py-2 rounded-lg text-xs text-center"
                                >
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-center gap-1.5 mt-4">
                    {departments.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setOrgSlideIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          index === orgSlideIndex ? 'bg-gold w-5' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
